import React, {useState, useMemo} from 'react';
import axios from 'axios';
import {motion} from 'framer-motion';

const RSVPForm = ({ member, family, deadline, token, onSuccess }) => {
    // Инициализируем статусы
    const [statuses, setStatuses] = useState(() => {
        const init = {[member.id]: member.is_attending ?? null};
        (family?.members ?? []).forEach((link) => {
            init[link.member.id] = link.member.is_attending ?? null;
        });
        return init;
    });

    // Сохраняем оригинальные статусы для сравнения - теперь как состояние
    const [originalStatuses, setOriginalStatuses] = useState(() => {
        const init = {[member.id]: member.is_attending ?? null};
        (family?.members ?? []).forEach((link) => {
            init[link.member.id] = link.member.is_attending ?? null;
        });
        return init;
    });

    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    // Используем ту же логику проверки дедлайна, что и в Hero.js
    const isPastDeadline = useMemo(() => {
        if (!deadline) return false;
        const deadlineDate = new Date(`${deadline}T23:59:59+03:00`);
        return new Date() > deadlineDate;
    }, [deadline]);

    const canVoteForAll = family?.members?.some(
        (link) => link.member.id === member.id && link.can_vote_for_others
    );

    // Проверяем, были ли изменения
    const hasChanges = useMemo(() => {
        return Object.keys(statuses).some(
            (memberId) => statuses[memberId] !== originalStatuses[memberId]
        );
    }, [statuses, originalStatuses]);

    const updateStatus = (memberId, attending) => {
        setStatuses({...statuses, [memberId]: attending});
    };

    const submit = async () => {
        // Если нет изменений, не отправляем запрос
        if (!hasChanges) {
            return;
        }

        setLoading(true);
        const choices = Object.entries(statuses).map(([id, is_attending]) => ({
            member_id: +id,
            is_attending,
        }));

        try {
            await axios.post(`/api/invite/rsvp/${token}`, choices);
            setShowSuccess(true);

            // Обновляем оригинальные статусы после успешной отправки
            setOriginalStatuses({...statuses});

             if (onSuccess) onSuccess();

            setTimeout(() => setShowSuccess(false), 3000);
        } catch (e) {
            alert('Ошибка при сохранении ответа');
        } finally {
            setLoading(false);
        }
    };

    if (isPastDeadline) {
        return (
            <div className="text-center py-8 sm:py-12">
                <div className="text-4xl sm:text-5xl mb-3 sm:mb-4 text-gray-400">⌛</div>
                <h3 className="text-xl sm:text-2xl font-display mb-2">Подтверждение закрыто</h3>
                <p className="text-gray-600 text-sm sm:text-base">Срок для подтверждения участия истек</p>
                <p className="text-gray-600 text-sm sm:text-base mt-2">
                    Вы проголосовали: {statuses[member.id] === true ? <strong>Прийду</strong> :
                    <strong>Не приду</strong>}
                </p>
            </div>
        );
    }

    let membersToShow = family?.members ?? [
        {member, is_attending: false, can_vote_for_others: false},
    ];

    if (!canVoteForAll) {
        membersToShow = membersToShow.filter(
            (link) => link.member.id === member.id
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-2 sm:px-4">
            {/* Уведомление о правах */}
            {canVoteForAll && (
                <motion.div
                    className="bg-gold-accent/10 border border-gold-accent/30 rounded-lg p-3 sm:p-4 mb-6"
                    initial={{opacity: 0, y: 10}}
                    animate={{opacity: 1, y: 0}}
                >
                    <p className="text-center text-gold-accent font-medium text-sm sm:text-base">
                        Вы можете дать ответ за всех членов вашей семьи
                    </p>
                </motion.div>
            )}

            {/* Карточки участников */}
            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {membersToShow.map((link) => (
                    <motion.div
                        key={link.member.id}
                        className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow"
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{delay: membersToShow.indexOf(link) * 0.1}}
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                            {/* Имя участника */}
                            <div className="flex-1">
                                <h4 className="text-lg sm:text-xl font-display">
                                    {link.member.first_name}{' '}
                                    {link.member.patronymic || ''}
                                </h4>
                                <p className="text-gray-500 text-xs sm:text-sm mt-1">
                                    {link.member.id === member.id ? 'Вы' : 'Член семьи'}
                                </p>
                            </div>

                            {/* Кнопки выбора */}
                            <div className="flex flex-col xs:flex-row gap-2 sm:gap-3 mt-3 sm:mt-0">
                                {/* Кнопка "Приду" */}
                                <button
                                    onClick={() => updateStatus(link.member.id, true)}
                                    disabled={!canVoteForAll && link.member.id !== member.id}
                                    className={`
                    px-4 sm:px-6 py-2 sm:py-3 rounded-lg border-2 font-medium transition-all text-sm sm:text-base
                    ${statuses[link.member.id] === true
                                        ? 'bg-black text-white border-black'
                                        : 'bg-white text-black border-gray-300 hover:border-black'
                                    }
                    ${(!canVoteForAll && link.member.id !== member.id)
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'hover:scale-105'
                                    }
                  `}
                                >
                                    Приду
                                </button>

                                {/* Кнопка "Не смогу" */}
                                <button
                                    onClick={() => updateStatus(link.member.id, false)}
                                    disabled={!canVoteForAll && link.member.id !== member.id}
                                    className={`
                    px-4 sm:px-6 py-2 sm:py-3 rounded-lg border-2 font-medium transition-all text-sm sm:text-base
                    ${statuses[link.member.id] === false
                                        ? 'bg-gray-800 text-white border-gray-800'
                                        : 'bg-white text-gray-800 border-gray-300 hover:border-gray-800'
                                    }
                    ${(!canVoteForAll && link.member.id !== member.id)
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'hover:scale-105'
                                    }
                  `}
                                >
                                    Не смогу
                                </button>
                            </div>
                        </div>

                        {/* Индикатор текущего выбора */}
                        <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
                            <p className="text-xs sm:text-sm text-gray-600">
                                {statuses[link.member.id] === true && (
                                    originalStatuses[link.member.id] === true
                                        ? '✅ Ваш текущий выбор: Приду'
                                        : '✅ Мы рады, что Вы намерены прийти'
                                )}
                                {statuses[link.member.id] === false && (
                                    originalStatuses[link.member.id] === false
                                        ? '❌ Ваш текущий выбор: Не смогу'
                                        : '❌ Может, всё-таки придете?('
                                )}
                                {statuses[link.member.id] === null && (
                                    originalStatuses[link.member.id] === null
                                        ? '⏳ Еще не ответили'
                                        : '⏳ Ответ изменен'
                                )}
                            </p>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Кнопка отправки */}
            <motion.div
                className="text-center px-2"
                initial={{opacity: 0, y: 10}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: 0.3}}
            >
                <button
                    onClick={submit}
                    disabled={loading || !hasChanges}
                    className={`
            relative
            px-8 sm:px-12 py-3 sm:py-4
            text-sm sm:text-lg uppercase tracking-widest
            border-2 transition-all duration-300
            overflow-hidden
            group
            w-full max-w-xs sm:max-w-none
            ${!hasChanges
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-default'
                        : 'bg-black text-white border-black hover:bg-white hover:text-black'
                    }
            ${(loading || !hasChanges) ? 'cursor-not-allowed' : ''}
          `}
                >
          <span className="relative z-10">
            {loading
                ? 'Отправка...'
                : !hasChanges
                    ? 'Текущий вариант'
                    : 'Подтвердить участие'
            }
          </span>
                    {hasChanges && !loading && (
                        <span className="
              absolute inset-0
              bg-white
              transform -translate-x-full
              group-hover:translate-x-0
              transition-transform duration-300
            "/>
                    )}
                </button>

                <p className="text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4">
                    Нажмите один раз. Ответ можно изменить до {new Date(deadline).toLocaleDateString('ru-RU')}
                </p>
            </motion.div>

            {/* Успешное сообщение */}
            {showSuccess && (
                <motion.div
                    className="fixed top-4 right-4 left-4 sm:left-auto sm:right-4 bg-green-500 text-white p-3 sm:p-4 rounded-lg shadow-lg flex items-center justify-between sm:justify-start sm:space-x-3 text-sm sm:text-base"
                    initial={{opacity: 0, x: 100}}
                    animate={{opacity: 1, x: 0}}
                    exit={{opacity: 0, x: 100}}
                >
                    <div className="flex items-center space-x-2 sm:space-x-3">
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                        </svg>
                        <span>Спасибо! Ваш ответ сохранен</span>
                    </div>
                    <button
                        onClick={() => setShowSuccess(false)}
                        className="ml-2 sm:hidden text-white"
                    >
                        ×
                    </button>
                </motion.div>
            )}
        </div>
    );
};

export default RSVPForm;